import { useCallback, useEffect, useState } from 'react';
import AutocompleteBlock from 'ui/autocompleteBlock';
import { IOption } from 'interfaces/IOption';
import { errorHandler } from 'utils/helpers/errorHandler';
import { errorToast, successToast } from 'utils/helpers/notify';
import { useMeRole } from 'store/hooks/useMeRole';
import { useObject } from 'utils/api/useObject';
import { Role } from 'generated/graphql';
import { useAddResponsibleToObjectMutation } from '../../api/mutations/generated/AddResponsibleToObject';
import { useUsersByRole } from 'utils/api/useUsersByRole';
import ResponsibleUser from '../responsibleUsers';
import { getInheritedResponsibles } from './helpers';
import { useParams } from 'react-router-dom';

const ManagersAndDirectorsBlock = () => {
  const { facilityId } = useParams();
  const { object, id, refetchObject } = useObject();
  const isMeAdminOrSupervisor = useMeRole([Role.Admin, Role.Supervisor]);
  const isMeDirector = useMeRole([Role.ClientDirector]);

  useEffect(() => {
    refetchObject();
  }, [facilityId, refetchObject]);

  const clientManagers = getInheritedResponsibles(object, Role.ClientManager);
  const clientDirectors = getInheritedResponsibles(object, Role.ClientDirector);

  const [manager, setManager] = useState<IOption | null>(null);
  const [director, setDirector] = useState<IOption | null>(null);

  const { users: directors } = useUsersByRole({
    role: Role.ClientDirector,
    limit: 20,
    offset: 0,
  });

  const { users: managers } = useUsersByRole({
    role: Role.ClientManager,
    limit: 20,
    offset: 0,
  });

  const [addResponsible, { loading }] = useAddResponsibleToObjectMutation({
    refetchQueries: ['GetObject'],
  });

  const addManagerHandler = useCallback(
    async (userId: string) => {
      if (!id) return;

      await addResponsible({
        variables: {
          input: {
            objectId: id,
            userId,
          },
        },
      })
        .then(() => {
          successToast('Менеджер успешно добавлен');
        })
        .catch(err => errorToast(errorHandler(err)));
    },
    [id, addResponsible],
  );

  const addDirectorHandler = useCallback(
    async (userId: string) => {
      if (!id) return;

      await addResponsible({
        variables: {
          input: {
            objectId: id,
            userId,
          },
        },
      })
        .then(() => {
          successToast('Директор успешно добавлен');
        })
        .catch(err => errorToast(errorHandler(err)));
    },
    [id, addResponsible],
  );

  const directorOptions =
    directors?.map(user => ({
      id: user.id,
      name: user.lastname,
    })) || [];

  const managerOptions =
    managers?.map(user => ({
      id: user.id,
      name: user.lastname,
    })) || [];

  return (
    <div className="col-span-full xl:col-span-9 md:col-span-7">
      <div className="facilityPageBlock blockTemplate">
        <div className="grid grid-cols-8 gap-5">
          <span className="Subtitle1 text-smena_text-secondary inline-block col-span-full">Представители</span>
          <div className="col-span-full grid grid-cols-8 gap-x-5 pb-10 mb-3 border-b border-smena_gray-30">
            <div className="sm:col-span-4 col-span-8">
              <div className="grid grid-cols-[max-content_1fr] gap-x-2 items-baseline">
                <span className="Subtitle2">Директор</span>
                <div className={'grid gap-y-1'}>
                  {clientDirectors?.length ? (
                    clientDirectors.map(clientDirector => (
                      <ResponsibleUser
                        key={clientDirector.id}
                        user={clientDirector}
                        canRemove={!clientDirector.isInherited}
                      />
                    ))
                  ) : (
                    <span className="Body2 text-smena_text-secondary">Не назначен</span>
                  )}
                </div>
              </div>
            </div>

            <div className="sm:col-span-4 col-span-8">
              <div className="grid grid-cols-[max-content_1fr] gap-x-2 items-baseline">
                <span className="Subtitle2">Менеджер</span>
                <div className={'grid gap-y-1'}>
                  {clientManagers?.length ? (
                    clientManagers.map(clientManager => (
                      <ResponsibleUser
                        key={clientManager.id}
                        user={clientManager}
                        canRemove={isMeAdminOrSupervisor && !clientManager.isInherited}
                      />
                    ))
                  ) : (
                    <span className="Body2 text-smena_text-secondary">Не назначен</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {(isMeDirector || isMeAdminOrSupervisor) && (
            <div className="col-span-full grid grid-cols-8 gap-x-5">
              <div className="sm:col-span-4 col-span-8">
                <AutocompleteBlock
                  id="directorId"
                  label="Директор"
                  value={director?.name}
                  onChangeHandler={value => setDirector(value)}
                  options={directorOptions}
                  buttonHandler={() => {
                    if (director) {
                      addDirectorHandler(director.id);
                    }
                  }}
                  loading={loading}
                />
              </div>
              <div className="sm:col-span-4 col-span-8">
                <AutocompleteBlock
                  id="managerId"
                  label="Менеджер"
                  value={manager?.name}
                  onChangeHandler={value => setManager(value)}
                  options={managerOptions}
                  buttonHandler={() => {
                    if (manager) {
                      addManagerHandler(manager.id);
                    }
                  }}
                  loading={loading}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagersAndDirectorsBlock;
